import { present } from '@art-suite/art-core-ts';
import { css } from '@emotion/react';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import { Button } from '../Button';
import { LabeledInputSize, LabeledInputStyle } from './LabeledInputCommonTypes';
import { useEverythingYouNeedForADropdown } from './LabeledInputLib';
import { LabeledInputPresenter } from './LabeledInputPresenter';

const PICKLIST_DROPDOWN_STYLE = css`
  label: PICKLIST_DROPDOWN_STYLE;
  position: fixed;
  background: var(--background);
  border: 1px solid var(--border2);
  max-height: 200px;
  overflow-y: auto;
  z-index: 99999;
  max-height: 300px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const TAG_CLOUD_CONTAINER_STYLE = css`
  label: TAG_CLOUD_CONTAINER_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: ${LAYOUT2.spacing1}px;
  margin-bottom: ${LAYOUT2.spacing1}px;
`;

const TAG_STYLE = css`
  label: TAG_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacingP5}px;
  padding: ${LAYOUT2.spacingP5}px ${LAYOUT2.spacing1}px;
  background: var(--background3);
  border: 1px solid var(--border2);
  ${TYPOGRAPHY2.xSmall}
  color: var(--text);
`;

const TAG_REMOVE_BUTTON_STYLE = css`
  label: TAG_REMOVE_BUTTON_STYLE;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text2);
  &:hover {
    color: var(--text);
  }
`;

const MULTI_PICKER_PLACEHOLDER_CONTAINER_STYLE = css`
  label: MULTI_PICKER_PLACEHOLDER_CONTAINER_STYLE;
  flex: 1;
  min-height: 44px;
  display: flex;
  flex-direction: column;
`;

const MULTI_PICKER_SEARCH_CONTAINER_STYLE = css`
  label: MULTI_PICKER_SEARCH_CONTAINER_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  align-items: center;
`;

const MULTI_PICKER_SEARCH_INPUT_STYLE = css`
  label: MULTI_PICKER_SEARCH_INPUT_STYLE;
  flex: 1;
  border: 0;
  padding: 0;
  height: 44px;
  ${TYPOGRAPHY2.xSmall}
  color: var(--text);
  background: var(--background);
  outline: none;
`;

const MULTI_PICKER_OPTION_STYLE = css`
  label: MULTI_PICKER_OPTION_STYLE;
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
  cursor: pointer;
  border-bottom: 1px solid var(--border2);
  ${TYPOGRAPHY2.xSmall}
  color: var(--text);
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;

  &:hover {
    background: var(--background2);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const MULTI_PICKER_CHECKBOX_STYLE = css`
  label: MULTI_PICKER_CHECKBOX_STYLE;
  width: 16px;
  height: 16px;
  border: 1px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MULTI_PICKER_CHECKBOX_CHECKED_STYLE = css`
  label: MULTI_PICKER_CHECKBOX_CHECKED_STYLE;
  background: var(--background3);
`;

const LINK_STYLE = css`
  label: LINK_STYLE;
  cursor: pointer;
  &:hover {
    background: var(--backgroundGreen2);
  }
`;

export type LabeledInputMultiPicklistItem = { value: string; label?: string; link?: string };

export type LabeledInputMultiPicklistProps = {
  addButtonLabel?: string;
  allowAddNew?: boolean;
  backgroundColor?: string;
  delay?: number;
  disabled?: boolean;
  isNewItemValid?: (item: string) => boolean;
  items?: LabeledInputMultiPicklistItem[];
  label?: string;
  onChange?: (values: string[]) => void;
  onUpdate?: (values: string[]) => void;
  onAddItem?: (value: string) => void;
  onRemoveItem?: (value: string) => void;
  placeholder?: string;
  size?: LabeledInputSize;
  style?: LabeledInputStyle;
  title?: string;
  value: string[];
  width?: string;
  optional?: boolean;
  addOnBlur?: boolean;
};

const getCanAddNew = (allowAddNew: boolean, searchText: string, selectedValues: string[], isNewItemValid: (value: string) => boolean) => {
  if (!allowAddNew) return false;
  if (!searchText) return false;
  if (!isNewItemValid(searchText)) return false;
  const lowerCaseSearchText = searchText.toLowerCase();
  const lowerCaseSelectedValues = selectedValues.map(value => value.toLowerCase());
  return !lowerCaseSelectedValues.includes(lowerCaseSearchText);
};

export const LabeledMultiSelect = ({
  title,
  label,
  value: selectedValues,
  placeholder = 'Add...',
  allowAddNew = false,
  items,
  disabled = false,
  style = 'normal',
  size = 'medium',
  width = '100%',
  delay = 500,
  onChange,
  onUpdate,
  onAddItem,
  onRemoveItem,
  backgroundColor,
  isNewItemValid = present,
  addButtonLabel = 'Add',
  optional = false,
}: LabeledInputMultiPicklistProps) => {
  const { dropdown, dropdownPositionStyling, containerRef, dropdownRef, isPicklistOpen, setIsPicklistOpen, forceUpdate } = useEverythingYouNeedForADropdown();
  // searchText is kept raw while typing so multi-word entries can be typed; trim where it's consumed
  const [searchText, setSearchText] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wasOpenBeforeMouseDownRef = useRef(false);
  selectedValues ||= [];
  const navigate = useNavigate();

  const debouncedUpdate = useDebouncedCallback(
    (updatedValues: string[]) => onUpdate?.(updatedValues),
    delay,
  );

  const handleOpen = () => {
    if (disabled || isPicklistOpen) return;
    setIsPicklistOpen(true);
    setSearchText('');
  };

  const handleClose = () => {
    setIsPicklistOpen(false);
    setSearchText('');
  };

  const updateValues = (newValues: string[]) => {
    onChange?.(newValues);
    debouncedUpdate(newValues);
  };

  const handleRemoveValue = (valueToRemove: string) => {
    const newValues = selectedValues.filter(v => v !== valueToRemove);
    updateValues(newValues);
    onRemoveItem?.(valueToRemove);
  };

  const handleToggleValue = (value: string) => {
    const isSelected = selectedValues.includes(value);
    const newValues = isSelected
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];

    if (isSelected) onRemoveItem?.(value);
    else onAddItem?.(value);

    updateValues(newValues);
    // Maintain focus on search input after selection
    searchInputRef.current?.focus();
  };

  const handleAddNew = (autoFocus = true) => {
    const trimmedText = searchText.trim();
    if (!present(trimmedText)) return;

    if (!getCanAddNew(allowAddNew, trimmedText, selectedValues, isNewItemValid)) return;

    onAddItem?.(trimmedText);
    updateValues([...selectedValues, trimmedText]);
    setSearchText('');
    if (autoFocus) setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  useEffect(() => {
    forceUpdate();
  }, [selectedValues, forceUpdate]);

  // Focus search input when opened
  useEffect(() => {
    if (isPicklistOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
    };
  }, [isPicklistOpen]);

  // Filter options based on search
  const filteredItems = useMemo(() => {
    const searchLower = searchText.trim().toLowerCase();
    if (!searchLower) return items;
    return items?.filter(opt => (opt.label || opt.value).toLowerCase().includes(searchLower));
  }, [items, searchText]);

  const canAddNew = useMemo(
    () => getCanAddNew(allowAddNew, searchText.trim(), selectedValues, isNewItemValid),
    [allowAddNew, searchText, selectedValues, isNewItemValid],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (allowAddNew && searchText.trim()) {
        // When add new is allowed, check for exact match first, otherwise add new
        const exactMatch = items?.find(
          opt => (opt.label || opt.value).toLowerCase() === searchText.trim().toLowerCase(),
        );
        if (exactMatch) {
          handleToggleValue(exactMatch.value);
        } else {
          handleAddNew();
        }
      } else if (filteredItems && filteredItems.length > 0) {
        // When add new is not allowed, just toggle the top item
        handleToggleValue(filteredItems[0].value);
      }
    } else if (event.key === 'Tab') {
      if (canAddNew) handleAddNew(false);
      // allow default tab behavior as well
    } else if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget as Node | null;
    if (relatedTarget && (containerRef.current?.contains(relatedTarget) || dropdownRef.current?.contains(relatedTarget))) {
      return;
    }
    if (canAddNew) handleAddNew(false);
    // Delay closing to allow click events on dropdown to fire first
    blurTimeoutRef.current = setTimeout(() => {
      handleClose();
    }, 150);
  };

  const handleDropdownMouseDown = (event: React.MouseEvent) => {
    // Cancel blur timeout if clicking on dropdown
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    event.preventDefault();
  };

  const handleInputMouseDown = () => {
    wasOpenBeforeMouseDownRef.current = isPicklistOpen;
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (wasOpenBeforeMouseDownRef.current && !searchText) {
      searchInputRef.current?.blur();
    }
  };

  // Get display labels for selected values
  const selectedTags = useMemo(() =>
    selectedValues.map(value => {
      const item = items?.find(opt => opt.value === value);
      return {
        value,
        label: item?.label || value,
        link: item?.link,
      };
    }), [selectedValues, items]);

  return (
    <div ref={containerRef} style={{ width }}>
      <LabeledInputPresenter
        width={width}
        style={style}
        size={size}
        backgroundColor={backgroundColor}
        onClick={() => {}}
        title={title || ''}
        isFocused={isPicklistOpen}
        label={label}
        optional={optional}
      >
        <div css={MULTI_PICKER_PLACEHOLDER_CONTAINER_STYLE}>
          {disabled && selectedTags.length > 0 && <div style={{ height: LAYOUT2.spacing1p5 }} />}
          {!disabled && (
            <div css={MULTI_PICKER_SEARCH_CONTAINER_STYLE}>
              <input
                ref={searchInputRef}
                css={MULTI_PICKER_SEARCH_INPUT_STYLE}
                value={searchText}
                onChange={event => setSearchText(event.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={handleOpen}
                onBlur={handleBlur}
                onMouseDown={handleInputMouseDown}
                onClick={handleInputClick}
                placeholder={placeholder ?? (allowAddNew ? 'Search or type new...' : 'Search...')}
              />
              {canAddNew && (
                <Button onClick={() => handleAddNew()}>
                  {addButtonLabel}
                </Button>
              )}
            </div>
          )}
          {selectedTags.length > 0 && (
            <div css={TAG_CLOUD_CONTAINER_STYLE}>
              {selectedTags.map(({ value, label, link }) => (
                <div
                  key={value}
                  css={[TAG_STYLE, link && LINK_STYLE]}
                  onClick={link
                    ? () => navigate(link)
                    : undefined}
                >
                  <div>{label}</div>
                  {!disabled && (
                    <div
                      css={TAG_REMOVE_BUTTON_STYLE}
                      onClick={event => {
                        event.stopPropagation();
                        handleRemoveValue(value);
                      }}
                    >
                      ✕
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </LabeledInputPresenter>
      {filteredItems && dropdown(() => (
        <div
          ref={dropdownRef}
          css={[
            PICKLIST_DROPDOWN_STYLE,
            dropdownPositionStyling,
          ]}
          onMouseDown={handleDropdownMouseDown}
        >
          <div style={{ overflowY: 'auto' }}>
            {filteredItems.length > 0
              ? (
                filteredItems.map(option => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      css={MULTI_PICKER_OPTION_STYLE}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleToggleValue(option.value);
                      }}
                    >
                      <div
                        css={[
                          MULTI_PICKER_CHECKBOX_STYLE,
                          isSelected && MULTI_PICKER_CHECKBOX_CHECKED_STYLE,
                        ]}
                      >
                        {isSelected && '✓'}
                      </div>
                      <div>{option.label || option.value}</div>
                    </div>
                  );
                })
              )
              : (
                <div
                  css={[
                    MULTI_PICKER_OPTION_STYLE,
                    { color: 'var(--text2)', fontStyle: 'italic' },
                  ]}
                >
                  {canAddNew
                    ? `Press Enter to add "${searchText}"`
                    : 'No options found'}
                </div>
              )}
          </div>
        </div>
      ))}
    </div>
  );
};

type FormikLabeledInputMultiPicklistProps = Omit<LabeledInputMultiPicklistProps, 'value' | 'onChange' | 'onUpdate'> & {
  name: string;
};

export const FormikLabeledMultiSelect = (labeledInputMultiPicklistProps: FormikLabeledInputMultiPicklistProps) => {
  const { values, setFieldValue } = useFormikContext<any>();
  return (
    <LabeledMultiSelect
      {...labeledInputMultiPicklistProps}
      value={values[labeledInputMultiPicklistProps.name] || []}
      onChange={values => setFieldValue(labeledInputMultiPicklistProps.name, values)}
    />
  );
};
